import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';
import { Shield, CreditCard, Truck, Package, MapPin, Mail } from 'lucide-react';
import AdminEmailConfig from '../../components/admin/AdminEmailConfig';
import AdminCustomerEmails from '../../components/admin/AdminCustomerEmails';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAccessConfig = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  // Stripe Config
  const [stripeConfig, setStripeConfig] = useState({
    test_publishable_key: '',
    test_secret_key: '',
    production_publishable_key: '',
    production_secret_key: '',
    webhook_secret: '',
    environment: 'test'
  });

  // FedEx Config
  const [fedexConfig, setFedexConfig] = useState({
    client_id: '',
    client_secret: '',
    account_number: '',
    environment: 'sandbox'
  });

  // Envía Config
  const [enviaConfig, setEnviaConfig] = useState({
    sandbox_token: '',
    production_token: '',
    environment: 'sandbox'
  });

  // Google Maps Config
  const [googleMapsConfig, setGoogleMapsConfig] = useState({
    api_key: '',
    is_active: false
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch Stripe config
      const stripeRes = await fetch(`${API_URL}/api/config/stripe`, { headers });
      if (stripeRes.ok) {
        const stripeData = await stripeRes.json();
        setStripeConfig(stripeData);
      }

      // Fetch FedEx config
      const fedexRes = await fetch(`${API_URL}/api/config/fedex`, { headers });
      if (fedexRes.ok) {
        const fedexData = await fedexRes.json();
        setFedexConfig(fedexData);
      }

      // Fetch Envía config
      const enviaRes = await fetch(`${API_URL}/api/config/envia`, { headers });
      if (enviaRes.ok) {
        const enviaData = await enviaRes.json();
        setEnviaConfig(enviaData);
      }

      // Fetch Google Maps config
      const mapsRes = await fetch(`${API_URL}/api/config/google-maps`, { headers });
      if (mapsRes.ok) {
        const mapsData = await mapsRes.json();
        setGoogleMapsConfig(mapsData);
      }
    } catch (error) {
      console.error('Error fetching configs:', error);
    }
  };

  const saveStripeConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/config/stripe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(stripeConfig)
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Stripe configuration saved successfully',
        });
      } else {
        throw new Error('Failed to save configuration');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveFedExConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/config/fedex`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(fedexConfig)
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'FedEx configuration saved successfully',
        });
      } else {
        throw new Error('Failed to save configuration');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveEnviaConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/config/envia`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(enviaConfig)
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Envía configuration saved successfully',
        });
      } else {
        throw new Error('Failed to save configuration');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveGoogleMapsConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/config/google-maps`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(googleMapsConfig)
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Google Maps configuration saved successfully',
        });
      } else {
        throw new Error('Failed to save configuration');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6">
        <h1 className="text-3xl font-serif text-[#2C2420] mb-2">Access Configuration</h1>
        <p className="text-gray-600">Manage API keys and credentials for payment and shipping services</p>
      </div>

      <Tabs defaultValue="stripe" className="w-full">
        <TabsList className="grid w-full grid-cols-5 mb-6">
          <TabsTrigger value="stripe">
            <CreditCard className="w-4 h-4 mr-2" />
            Stripe
          </TabsTrigger>
          <TabsTrigger value="fedex">
            <Package className="w-4 h-4 mr-2" />
            FedEx
          </TabsTrigger>
          <TabsTrigger value="envia">
            <Truck className="w-4 h-4 mr-2" />
            Envía
          </TabsTrigger>
          <TabsTrigger value="google-maps">
            <MapPin className="w-4 h-4 mr-2" />
            Google Maps
          </TabsTrigger>
          <TabsTrigger value="email">
            <Mail className="w-4 h-4 mr-2" />
            Email/SMTP
          </TabsTrigger>
        </TabsList>

        {/* Stripe Configuration */}
        <TabsContent value="stripe">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Stripe Payment Configuration
              </CardTitle>
              <CardDescription>
                Configure your Stripe API keys for payment processing. Test keys are used for development, production keys for live transactions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="stripe-environment">Environment</Label>
                <Select
                  value={stripeConfig.environment}
                  onValueChange={(value) => setStripeConfig({ ...stripeConfig, environment: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="test">Test (Development)</SelectItem>
                    <SelectItem value="production">Production (Live)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="test-publishable">Test Publishable Key</Label>
                  <Input
                    id="test-publishable"
                    type="text"
                    placeholder="pk_test_..."
                    value={stripeConfig.test_publishable_key}
                    onChange={(e) => setStripeConfig({ ...stripeConfig, test_publishable_key: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="test-secret">Test Secret Key</Label>
                  <Input
                    id="test-secret"
                    type="password"
                    placeholder="sk_test_..."
                    value={stripeConfig.test_secret_key}
                    onChange={(e) => setStripeConfig({ ...stripeConfig, test_secret_key: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="prod-publishable">Production Publishable Key</Label>
                  <Input
                    id="prod-publishable"
                    type="text"
                    placeholder="pk_live_..."
                    value={stripeConfig.production_publishable_key}
                    onChange={(e) => setStripeConfig({ ...stripeConfig, production_publishable_key: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="prod-secret">Production Secret Key</Label>
                  <Input
                    id="prod-secret"
                    type="password"
                    placeholder="sk_live_..."
                    value={stripeConfig.production_secret_key}
                    onChange={(e) => setStripeConfig({ ...stripeConfig, production_secret_key: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="webhook-secret">Webhook Secret (Optional)</Label>
                <Input
                  id="webhook-secret"
                  type="password"
                  placeholder="whsec_..."
                  value={stripeConfig.webhook_secret}
                  onChange={(e) => setStripeConfig({ ...stripeConfig, webhook_secret: e.target.value })}
                />
                <p className="text-sm text-gray-500">
                  Used to verify webhook events from Stripe
                </p>
              </div>

              <Button
                onClick={saveStripeConfig}
                disabled={loading}
                className="w-full bg-[#be1f1a] hover:bg-[#a71b17]"
              >
                {loading ? 'Saving...' : 'Save Stripe Configuration'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FedEx Configuration */}
        <TabsContent value="fedex">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                FedEx Shipping Configuration
              </CardTitle>
              <CardDescription>
                Configure your FedEx API credentials for real-time shipping rates and label generation.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fedex-environment">Environment</Label>
                <Select
                  value={fedexConfig.environment}
                  onValueChange={(value) => setFedexConfig({ ...fedexConfig, environment: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox (Testing)</SelectItem>
                    <SelectItem value="production">Production (Live)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fedex-client-id">Client ID</Label>
                <Input
                  id="fedex-client-id"
                  type="text"
                  placeholder="Your FedEx API Client ID"
                  value={fedexConfig.client_id}
                  onChange={(e) => setFedexConfig({ ...fedexConfig, client_id: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fedex-secret">Client Secret</Label>
                <Input
                  id="fedex-secret"
                  type="password"
                  placeholder="Your FedEx API Client Secret"
                  value={fedexConfig.client_secret}
                  onChange={(e) => setFedexConfig({ ...fedexConfig, client_secret: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fedex-account">Account Number</Label>
                <Input
                  id="fedex-account"
                  type="text"
                  placeholder="Your FedEx Account Number"
                  value={fedexConfig.account_number}
                  onChange={(e) => setFedexConfig({ ...fedexConfig, account_number: e.target.value })}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                <p className="font-semibold text-blue-900 mb-1">How to get FedEx API credentials:</p>
                <ol className="list-decimal list-inside text-blue-800 space-y-1">
                  <li>Visit <a href="https://developer.fedex.com" target="_blank" rel="noopener noreferrer" className="underline">developer.fedex.com</a></li>
                  <li>Create a developer account</li>
                  <li>Register a new application to get Client ID and Secret</li>
                  <li>Use your FedEx account number for shipping</li>
                </ol>
              </div>

              <Button
                onClick={saveFedExConfig}
                disabled={loading}
                className="w-full bg-[#be1f1a] hover:bg-[#a71b17]"
              >
                {loading ? 'Saving...' : 'Save FedEx Configuration'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Envía Configuration */}
        <TabsContent value="envia">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Envía Shipping Configuration
              </CardTitle>
              <CardDescription>
                Configure your Envía API credentials for Colombia domestic shipping.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="envia-environment">Environment</Label>
                <Select
                  value={enviaConfig.environment}
                  onValueChange={(value) => setEnviaConfig({ ...enviaConfig, environment: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox (Testing)</SelectItem>
                    <SelectItem value="production">Production (Live)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="envia-sandbox-token">Sandbox Token</Label>
                <Input
                  id="envia-sandbox-token"
                  type="password"
                  placeholder="Your Envía Sandbox API Token"
                  value={enviaConfig.sandbox_token}
                  onChange={(e) => setEnviaConfig({ ...enviaConfig, sandbox_token: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="envia-prod-token">Production Token</Label>
                <Input
                  id="envia-prod-token"
                  type="password"
                  placeholder="Your Envía Production API Token"
                  value={enviaConfig.production_token}
                  onChange={(e) => setEnviaConfig({ ...enviaConfig, production_token: e.target.value })}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                <p className="font-semibold text-blue-900 mb-1">How to get Envía API credentials:</p>
                <ol className="list-decimal list-inside text-blue-800 space-y-1">
                  <li>Visit <a href="https://www.envia.com" target="_blank" rel="noopener noreferrer" className="underline">envia.com</a></li>
                  <li>Sign up for a merchant account</li>
                  <li>Go to API Settings in your dashboard</li>
                  <li>Generate API tokens for sandbox and production</li>
                </ol>
              </div>

              <Button
                onClick={saveEnviaConfig}
                disabled={loading}
                className="w-full bg-[#be1f1a] hover:bg-[#a71b17]"
              >
                {loading ? 'Saving...' : 'Save Envía Configuration'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Google Maps Configuration */}
        <TabsContent value="google-maps">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Google Maps API Configuration
              </CardTitle>
              <CardDescription>
                Configure Google Maps API for address autocomplete and validation in checkout.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="google-maps-key">Google Maps API Key</Label>
                <Input
                  id="google-maps-key"
                  type="password"
                  placeholder="Your Google Maps API Key"
                  value={googleMapsConfig.api_key}
                  onChange={(e) => setGoogleMapsConfig({ ...googleMapsConfig, api_key: e.target.value })}
                />
                <p className="text-sm text-gray-500">
                  API key with Places API and Maps JavaScript API enabled
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="maps-active"
                  checked={googleMapsConfig.is_active}
                  onChange={(e) => setGoogleMapsConfig({ ...googleMapsConfig, is_active: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="maps-active" className="font-normal cursor-pointer">
                  Enable Google Maps address autocomplete in checkout
                </Label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                <p className="font-semibold text-blue-900 mb-1">How to get Google Maps API key:</p>
                <ol className="list-decimal list-inside text-blue-800 space-y-1">
                  <li>Visit <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                  <li>Create a new project or select existing one</li>
                  <li>Enable "Places API" and "Maps JavaScript API"</li>
                  <li>Go to Credentials → Create Credentials → API Key</li>
                  <li>Restrict the key to your domain for security</li>
                </ol>
              </div>

              <Button
                onClick={saveGoogleMapsConfig}
                disabled={loading}
                className="w-full bg-[#be1f1a] hover:bg-[#a71b17]"
              >
                {loading ? 'Saving...' : 'Save Google Maps Configuration'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email/SMTP Configuration */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Email/SMTP Configuration
              </CardTitle>
              <CardDescription>
                Configure email settings for order confirmations and notifications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AdminEmailConfig />
            </CardContent>
          </Card>
          <AdminCustomerEmails />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminAccessConfig;

